import { action, computed, observable, runInAction } from 'mobx'
import { getAddress, isAddress } from 'viem'
import { z } from 'zod'
import { getImage } from '@/utils/media.js'
import CategoryModel from '@/models/CategoryModel.js'
import { clearPersistedStore, makePersistable } from 'mobx-persist-store'

export default class UserModel {
  rootStore = null

  @observable accessor about = ''
  @observable accessor address = ''
  @observable accessor affiliateId = ''
  @observable accessor avatar = ''
  @observable accessor country = ''
  @observable accessor cover = ''
  @observable accessor createdAt = ''
  @observable accessor email = ''
  @observable accessor handle = ''
  @observable accessor name = ''
  @observable accessor isLive = false
  @observable accessor nickname = ''
  @observable accessor customNickname = ''
  @observable accessor isFavorite = false
  @observable accessor postCount = 0
  @observable accessor visitors = 0
  @observable accessor visits = 0
  @observable accessor categories = observable.map()

  // FIXME: to be removed once BE is redone as they are needed for compatibility now
  @observable accessor explicit = false
  @observable accessor defaultRoomName = ''
  @observable accessor defaultPrice = 0

  // TODO: properly format dates and numbers based on locale (needs i18n)

  constructor(rootStore, userData, persist = false) {
    this.rootStore = rootStore
    this.update(userData)

    if (!this.hasStore && persist)
      makePersistable(this, {
        name: 'user',
        properties: ['customNickname'],
      }).then()
    else clearPersistedStore(this).then()
  }

  reset = () => {
    this.categories.clear()
  }

  get analytics() {
    return this.rootStore.analyticsStore
  }

  get api() {
    return this.rootStore.apiStore
  }

  get auth() {
    return this.rootStore.authStore
  }

  get exchange() {
    return this.rootStore.exchangeStore
  }

  @computed
  get isAuthUser() {
    return this.handle === this.auth.user?.handle
  }

  @computed
  get hasStore() {
    return Boolean(this.handle)
  }

  @computed
  get avatarFallback() {
    return this.hasStore
      ? this.nickname
          .split(' ')
          .slice(0, 2)
          .map(x => x[0])
          .join('')
      : '0x'
  }

  @computed
  get createdAtFormatted() {
    return new Date(this.createdAt).toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    })
  }

  @computed
  get hasEmail() {
    return z.email().safeParse(this.email).success
  }

  @computed
  get canUseCoinbaseOnramp() {
    // TODO: should retrieve latest list as there are more countries available
    return this.country === 'us'
  }

  @computed
  get currency() {
    return this.exchange.getCurrencyOfCountry(this.country)
  }

  @computed
  get categoriesArray() {
    return Array.from(this.categories.values()).sort((a, b) => a.name.localeCompare(b.name))
  }

  @computed
  get publishedCategoriesArray() {
    return this.categoriesArray.filter(x => x.published)
  }

  @computed
  get postCreationCategoriesCombobox() {
    const items = this.categoriesArray.map(x => x.comboboxItem)

    items.unshift({
      label: 'No category',
      noValue: true,
      value: '-1',
    })

    return items
  }

  @computed
  get categoriesManagementCombobox() {
    const items = this.categoriesArray.map(x => x.comboboxItem)

    items.unshift({
      label: 'Create new category',
      noValue: true,
      value: '-1',
    })

    return items
  }

  @action
  update = userData => {
    // NOT USED: from login agreeTCandPP2, Boolean(canBook), country_code, currentPlan, markup, myReferral, promotion, ref_id, role, status
    // NOT USED: from getUser book_id, current_team_id, member, reference, userid

    this.about = userData.about ?? this.about

    const address = userData.address || userData.owner
    if (isAddress(address)) this.address = getAddress(address)

    this.affiliateId = userData.aff_id ?? this.affiliateId
    this.avatar = userData.customProfileImage ?? userData.image ?? this.avatar
    this.country = (userData.country_code ?? this.country).toLowerCase()
    this.cover = userData.cover ?? this.cover
    this.createdAt = userData.created_at ?? this.createdAt
    this.email = userData.email ?? this.email
    this.handle = userData.handle ?? this.handle
    this.isFavorite = userData.favorites !== undefined ? userData.favorites === 0 : this.isFavorite
    this.name = userData.name ?? this.name
    this.nickname = userData.nickname ?? this.nickname
    this.postCount = userData.nbpost ?? this.postCount
    this.visitors = userData.users ?? this.visitors
    this.visits = Number(userData.visits ?? this.visits) // TODO: do not return as string from backend

    this.explicit = Boolean(userData.explicit ?? this.explicit)
    this.defaultRoomName = userData.defaultRoomName ?? this.defaultRoomName
    this.defaultPrice = Number(userData.defaultPrice ?? this.defaultPrice)

    this.#updateNickname()
  }

  updateCategories = async () => {
    const response = await this.api.getCategories(this.address)

    if (!response?.data) return

    runInAction(() => {
      response.data.forEach(x => {
        this.categories.set(Number(x.categoryId ?? x.id), new CategoryModel(this.rootStore, x))
      })
    })
  }

  @action
  setCustomNickname = x => {
    this.customNickname = x
    this.#updateNickname()
  }

  toggleFavorite = async () => {
    if (this.isAuthUser) return false

    const response = await this.api.toggleFavorite(this.address)
    if (response?.data?.status !== 'success') return false

    runInAction(() => {
      this.isFavorite = !this.isFavorite
    })

    return true
  }

  updateViaForm = async values => {
    if (!this.isAuthUser) return false

    values.avatar = await getImage(values.avatar)
    values.cover = await getImage(values.cover)

    const response = await this.api.updateProfile(values)
    if (response?.data?.status !== 'success') return false

    // if (values.avatar !== this.avatar) this.analytics.customImageSaved()?.then()
    // if (values.cover !== this.cover) this.analytics.customCoverSaved()?.then()
    // if (values.nickname !== this.nickname) this.analytics.nicknameSaved()?.then()

    this.analytics.saveProfile()?.then()

    runInAction(() => {
      this.about = values.about
      this.avatar = values.avatar
      this.cover = values.cover
      this.nickname = values.nickname
    })

    return true
  }

  deleteAccount = async () => {
    if (!this.isAuthUser) return false

    const response = await this.api.deleteAccount()

    return response?.data?.status === 'success'
  }

  createCategory = async values => {
    if (!this.isAuthUser) return false

    const response = await this.api.createCategory(values)
    if (response?.data?.status !== 'OK') return false

    runInAction(() => {
      values.id = response.data.categoryId
      this.categories.set(values.id, new CategoryModel(this.rootStore, values))
    })

    return true
  }

  deleteCategory = async id => {
    if (!this.isAuthUser) return false

    const response = await this.api.deleteCategory(id)
    if (response?.data?.status !== 'OK') return false

    runInAction(() => {
      this.categories.delete(id)
    })

    return true
  }

  @action
  #updateNickname = () => {
    if (this.hasStore) {
      this.customNickname = ''
      clearPersistedStore(this).then()
    } else
      this.nickname =
        this.customNickname.length > 0 ? `${this.customNickname} (${this.address})` : this.address
  }
}
